import React from 'react';
import Fab, { FabProps } from '@mui/material/Fab';
import { IconProps } from '@mui/material/Icon';
import { useTheme } from '@mui/material/styles';
import { CircularProgress, SxProps, Theme } from '@mui/material';

// Extending FabProps to include all default Fab properties
interface WhiteFabProps extends FabProps {
  icon: React.ReactElement<IconProps>;
  backgroundColor?: 'primary' | 'error' | string;
  textColor: 'primary' | 'error' | string;
  loading?: boolean;
  invertColors?: boolean;
}

const WhiteFab: React.FC<WhiteFabProps> = ({ icon, backgroundColor, textColor, loading, invertColors, ...rest }) => {
  const theme = useTheme();

  const getThemeColor = (color: string) => {
    if (color === 'primary' || color === 'error') {
      return theme.palette[color].main;
    }
    return color;
  };

  let bgColor = backgroundColor ? getThemeColor(backgroundColor) : '#fff';
  let txtColor = getThemeColor(textColor);

  if (invertColors) {
    const temp = bgColor;
    bgColor = txtColor;
    txtColor = temp;
  }

  // Pull out sx so consumers can override defaults (including z-index) cleanly
  const { sx, ...fabProps } = rest;
  const mergedSx: SxProps<Theme> = sx
    ? Array.isArray(sx)
      ? [
          {
            boxShadow: 2,
            color: txtColor,
            backgroundColor: bgColor,
            '&:hover': {
              backgroundColor: txtColor,
              color: bgColor,
            },
          },
          ...sx,
        ]
      : [
          {
            boxShadow: 2,
            color: txtColor,
            backgroundColor: bgColor,
            '&:hover': {
              backgroundColor: txtColor,
              color: bgColor,
            },
          },
          sx,
        ]
    : {
        boxShadow: 2,
        color: txtColor,
        backgroundColor: bgColor,
        '&:hover': {
          backgroundColor: txtColor,
          color: bgColor,
        },
      };

  return (
    <Fab {...fabProps} sx={mergedSx}>
      {loading ? <CircularProgress size={20} color='inherit' /> : icon}
    </Fab>
  );
};

export default WhiteFab;
